import React, { FC, useCallback, useId } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import FancyCheckbox from '@rover/components/src/formFields/FancyCheckbox';
import Fieldset from '@rover/components/src/formFields/Fieldset';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import { PetType } from '@rover/types/src/Pet';

export interface Props {
  label: string;
  onChange: (petTypes: PetType[]) => void;
  searchFilters: {
    petType: PetType[];
    [other: string]: any;
  };
  layout?: 'column' | 'row';
}

const PetTypeFilterWrapper = styled.div<{ layout?: string }>`
  display: flex;
  flex-direction: ${({ layout }) => layout || 'column'};
`;

const StyledFancyCheckbox = styled(FancyCheckbox)<{ layout?: string }>`
  display: inline-flex !important;
  margin-bottom: 0 !important;
  &:not(:last-child) {
    margin-right: ${({ theme }) => theme.space['4x']};
  }
`;

const StyledFilterLabel = styled(FilterLabel)<{ layout?: string }>`
  width: auto;
  margin-right: ${({ layout, theme }) => (layout === 'row' ? theme.space['4x'] : 0)};
`;

const PetTypeFilter: FC<Props> = ({ label, onChange, searchFilters, layout }) => {
  const { t } = useLingui();
  const petTypeId = useId();
  const catTypeId = useId();
  const dogTypeId = useId();

  const selectedTypes = searchFilters.petType;

  const handleChange = useCallback(
    (petType: PetType): void => {
      if (selectedTypes.includes(petType)) {
        onChange(selectedTypes.filter((p) => p !== petType));
      } else {
        onChange([...selectedTypes, petType]);
      }
    },
    [onChange, selectedTypes]
  );

  return (
    <PetTypeFilterWrapper layout={layout}>
      <StyledFilterLabel
        id={petTypeId}
        layout={layout}
        as="div"
        data-testid="pet-type-filter-label"
      >
        {label}
      </StyledFilterLabel>
      <Fieldset aria-labelledby={petTypeId}>
        <StyledFancyCheckbox
          checked={selectedTypes.includes(PetType.DOG)}
          id={dogTypeId}
          label={t`Dog`}
          layout={layout}
          onChange={() => handleChange(PetType.DOG)}
          data-qa-id="dog-pet-type-selector"
          fontSize="100"
          labelMarginLeft={layout === 'row' ? '2x' : '4x'}
        />
        <StyledFancyCheckbox
          checked={selectedTypes.includes(PetType.CAT)}
          id={catTypeId}
          layout={layout}
          label={t`Cat`}
          onChange={() => handleChange(PetType.CAT)}
          data-qa-id="cat-pet-type-selector"
          fontSize="100"
          labelMarginLeft={layout === 'row' ? '2x' : '4x'}
        />
      </Fieldset>
    </PetTypeFilterWrapper>
  );
};

export default PetTypeFilter;
